import type {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import type {MolstarWrapperProps} from 'molbio/molstar/Viewer';

// Keep in sync with MAX_FILE_SIZE_FOR_PREVIEW_BYTES in benchling/external_files/mixins/previewable.py
const ONE_MEGABYTE = 1024 * 1024;
export const MAX_FILE_SIZE_FOR_PREVIEW_BYTES = 100 * ONE_MEGABYTE;

export const DISPLAY_ROW_LIMIT = 1000;

const UNKNOWN_BINARY_MIME_TYPE = 'application/octet-stream';
const PLAIN_TEXT_MIME_TYPE = 'text/plain';

export const MOLSTAR_MIME_TYPE_TO_FORMAT: Record<string, MolstarWrapperProps['format']> = {
  'chemical/x-pdb': 'pdb',
  'chemical/x-pdbqt': 'pdbqt',
  'chemical/x-mmcif': 'mmcif',
};

export const TEXT_MIME_TYPES = new Set(['application/json', 'application/yaml', PLAIN_TEXT_MIME_TYPE]);

export const CSV_MIME_TYPES = new Set(['text/csv', 'text/tsv']);

export const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

export const PDF_MIME_TYPE = 'application/pdf';

export const CODE_MIME_TYPES: Record<string, MonacoLanguage> = {
  'text/x-python-script': 'python',
  'text/x-python': 'python',
};

const FILENAME_EXTENSION_MIME_TYPES: Record<string, string> = {
  '.cif': 'chemical/x-mmcif',
  '.log': 'text/plain',
  '.mmcif': 'chemical/x-mmcif',
  '.pdb': 'chemical/x-pdb',
  '.pdbqt': 'chemical/x-pdbqt',
  '.pdbx': 'chemical/x-mmcif',
  '.pdf': PDF_MIME_TYPE,
};

function getPreviewMimeTypeFromFilename(filename?: string): string | null {
  if (filename == null) {
    return null;
  }
  const lower = filename.toLowerCase();
  for (const [extension, extensionMimeType] of Object.entries(FILENAME_EXTENSION_MIME_TYPES)) {
    if (lower.endsWith(extension)) {
      return extensionMimeType;
    }
  }
  return null;
}

function isSupportedPreviewMimeType(mimeType: string): boolean {
  return (
    TEXT_MIME_TYPES.has(mimeType) ||
    CSV_MIME_TYPES.has(mimeType) ||
    IMAGE_MIME_TYPES.has(mimeType) ||
    CODE_MIME_TYPES[mimeType] != null ||
    MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType] != null ||
    mimeType === PDF_MIME_TYPE ||
    mimeType === 'text/markdown' ||
    mimeType === 'text/html'
  );
}

function isMissingOrGenericMimeType(mimeType: string): boolean {
  return mimeType === '' || mimeType === UNKNOWN_BINARY_MIME_TYPE;
}

function isMolstarMimeType(mimeType: string): boolean {
  return MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType] != null;
}

function shouldPreferMolstarFilenameMimeType(filenameMimeType: string, mimeType: string): boolean {
  // Structure files are text-based and commonly arrive as text/plain, but render better in Molstar.
  return isMolstarMimeType(filenameMimeType) && mimeType === PLAIN_TEXT_MIME_TYPE;
}

function shouldPreferFilenameMimeType(filenameMimeType: string, mimeType: string | null): boolean {
  if (mimeType == null) {
    return true;
  }
  if (isMissingOrGenericMimeType(mimeType)) {
    return true;
  }
  if (!isSupportedPreviewMimeType(mimeType)) {
    return true;
  }
  if (shouldPreferMolstarFilenameMimeType(filenameMimeType, mimeType)) {
    return true;
  }
  return false;
}

export function getSupportedPreviewMimeType(
  mimeType: string | null | undefined,
  filename?: string
): string | null {
  const normalizedMimeType = mimeType?.trim().toLowerCase() || null;
  const filenameMimeType = getPreviewMimeTypeFromFilename(filename);
  if (filenameMimeType != null && shouldPreferFilenameMimeType(filenameMimeType, normalizedMimeType)) {
    return filenameMimeType;
  }
  if (normalizedMimeType != null && isSupportedPreviewMimeType(normalizedMimeType)) {
    return normalizedMimeType;
  }
  return filenameMimeType;
}

export function isSupportedMimeType(mimeType: string | null | undefined, filename?: string): boolean {
  return getSupportedPreviewMimeType(mimeType, filename) != null;
}
